import {
  TEXTS,
  pickMaxSeniorityCheck,
  sameMax,
  buildStampRequest,
  interpretStampResponse,
  stampOutcomeMessage,
  stampErrorMessage
} from '@/custom/classes/max_seniority_check.js'

// "Re-checked maximum seniority" state for a position modal. Expects
// `positionData` on the host; ModalPosition is the only place the maximum is
// edited. Wire it as:
//   initMaxSeniorityCheck(position)                    after the position loads
//   <MaxSeniority @save-start/@saved/@save-failed>     -> the three handlers below
//   <MaxSeniorityCheck @confirm="confirmMaxSeniority"> -> the stamp
// maxCheck === null means an older server (fields absent): nothing is rendered.
export default {
  data () {
    return {
      maxCheck: null,
      // the maximum the server last confirmed — the only value ever stamped
      maxSavedValue: null,
      maxCheckBusy: false,
      maxCheckMessage: '',
      maxSaving: false,
      // bumped by every stamp / save-refresh; an answer whose seq is no longer
      // current (or whose position is no longer in the modal) is dropped
      maxCheckSeq: 0,
      maxStampToken: 0,
      maxSaveLatest: 0
    }
  },
  methods: {
    initMaxSeniorityCheck (position) {
      this.maxCheck = pickMaxSeniorityCheck(position)
      this.maxSavedValue = position && position.max_seniority !== undefined ? position.max_seniority : null
    },
    isMaxCheckStale (seq, positionId) {
      return this._isDestroyed || seq !== this.maxCheckSeq || !this.positionData || this.positionData.id !== positionId
    },
    applyServerMaxToInput (value) {
      const fields = this.form && this.form.data && this.form.data.position ? this.form.data.position.fields : null
      if (fields && fields.max_seniority) fields.max_seniority.value = value === null || value === undefined ? '' : value
    },
    onMaxSenioritySaveStart ({ seq }) {
      this.maxSaveLatest = seq
      this.maxSaving = true
    },
    onMaxSenioritySaveFailed ({ seq }) {
      if (seq === this.maxSaveLatest) this.maxSaving = false
    },
    async onMaxSenioritySaved ({ seq, value, response }) {
      if (seq !== this.maxSaveLatest) return
      this.maxSaving = false
      if (!this.maxCheck) return // older server: nothing to keep in sync
      this.maxSavedValue = value
      this.maxCheckMessage = ''
      const checkSeq = ++this.maxCheckSeq
      const fromResponse = pickMaxSeniorityCheck(response)
      if (fromResponse) {
        this.maxCheck = fromResponse
        if ('max_seniority' in response) {
          this.maxSavedValue = response.max_seniority
          this.syncRoundedMaxToInput(response.max_seniority, value)
        }
        return
      }
      // PUT /positions/:id answers 204 — re-read the four fields.
      await this.refreshMaxSeniorityCheck(checkSeq, false, value)
    },
    // The server stored a different number than was typed (e.g. 5.5 -> 5):
    // show the stored value, but only while the input still holds what was saved.
    syncRoundedMaxToInput (serverMax, typedValue) {
      const fields = this.form && this.form.data && this.form.data.position ? this.form.data.position.fields : null
      const input = fields && fields.max_seniority ? fields.max_seniority.value : undefined
      if (!sameMax(serverMax, typedValue) && sameMax(input, typedValue)) this.applyServerMaxToInput(serverMax)
    },
    async refreshMaxSeniorityCheck (seq, syncInput, typedValue) {
      const positionId = this.positionData && this.positionData.id
      try {
        const position = await this.$network_utils.getPosition({ id: positionId })
        if (this.isMaxCheckStale(seq, positionId)) return
        const check = pickMaxSeniorityCheck(position)
        if (!check) return
        this.maxCheck = check
        this.maxSavedValue = position.max_seniority !== undefined ? position.max_seniority : null
        if (syncInput) this.applyServerMaxToInput(this.maxSavedValue)
        else if (typedValue !== undefined) this.syncRoundedMaxToInput(this.maxSavedValue, typedValue)
      } catch (e) {
        // keep what is on screen — the save itself succeeded
      }
    },
    async confirmMaxSeniority () {
      if (!this.maxCheck || this.maxCheckBusy || !this.positionData) return
      const positionId = this.positionData.id
      const seq = ++this.maxCheckSeq
      this.maxStampToken = seq
      this.maxCheckBusy = true
      this.maxCheckMessage = ''
      try {
        const response = await this.$network_utils.confirmMaxSeniorityChecks(buildStampRequest(positionId, this.maxSavedValue))
        if (this.isMaxCheckStale(seq, positionId)) return
        const outcome = interpretStampResponse(response, positionId)
        if (outcome.outcome === 'stamped') {
          this.maxCheck = outcome.check
          if (outcome.max !== undefined) this.maxSavedValue = outcome.max
          this.$utils.emit('show_toast', { text: TEXTS.stamped, type: 'is-success' })
          return
        }
        if (outcome.outcome === 'invalid') {
          this.$utils.emit('show_toast', { text: TEXTS.generic, type: 'is-danger' })
          return
        }
        this.maxCheckMessage = stampOutcomeMessage(outcome)
        if (outcome.outcome === 'max_changed' || outcome.outcome === 'no_max') {
          await this.refreshMaxSeniorityCheck(++this.maxCheckSeq, true)
        }
        // someone else already stamped this value: re-read the four fields, no error
        if (outcome.outcome === 'already_checked') {
          await this.refreshMaxSeniorityCheck(++this.maxCheckSeq, false)
        }
      } catch (error) {
        if (this.isMaxCheckStale(seq, positionId)) return
        this.$utils.emit('show_toast', { text: stampErrorMessage(error), type: 'is-danger' })
      } finally {
        if (this.maxStampToken === seq && !this._isDestroyed) this.maxCheckBusy = false
      }
    }
  }
}
